import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { SCREENS } from "../../constants";
import { useApp } from "../../provider";
import { EMPTY_ITEM, SHIFT_MOVE_TO } from "./constants";

const InventoryContext = createContext();
const useInventory = () => useContext(InventoryContext);

const InventoryProvider = ({
  type,
  children,
  pickUpCallback = () => {},
  allowRightClickPickup = true,
  canPutDown = () => true,
}) => {
  const {
    items,
    screen,
    insertInventoryItem,
    heldItem,
    setHeldItem,
    setMousePosition,
    setIsLeftDragging,
    clickReference,
    setInventory,
    setClickReference,
    handleMouseLeave,
    inventories,
    readonly,
  } = useApp();

  const inventory = inventories[type];
  const [lastMouseDownSlotIndex, setLastMouseDownSlotIndex] =
    useState(undefined);
  const [checkDClickIndex, setCheckDClickIndex] = useState(undefined);
  const [isLastRightClick, setIsLastRightClick] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedSlots, setDraggedSlots] = useState([]);
  const [dragRemainder, setDragRemainder] = useState(-1);
  const [dragStash, setDragStash] = useState([]);

  const timeoutDClick = useCallback(() => {
    setTimeout(() => setCheckDClickIndex(undefined), 250);
  }, []);

  const placeOneItem = useCallback(
    (newInventory, newHeldItem, index) => {
      if (newInventory[index].id === null) {
        newInventory[index] = { id: newHeldItem.id, count: 1 };
        newHeldItem =
          newHeldItem.count > 1
            ? { ...newHeldItem, count: newHeldItem.count - 1 }
            : EMPTY_ITEM;
      } else if (newInventory[index].id === newHeldItem.id) {
        const totalCount = newInventory[index].count + 1;
        if (totalCount <= items[newHeldItem.id].stackSize) {
          newInventory[index] = { id: newHeldItem.id, count: totalCount };
          newHeldItem =
            newHeldItem.count > 1
              ? { ...newHeldItem, count: newHeldItem.count - 1 }
              : EMPTY_ITEM;
        }
      }
      return [newInventory, newHeldItem];
    },
    [items]
  );

  const handleDrag = useCallback(
    (index, syncLastRightClick) => {
      if (readonly) return;
      if (heldItem.id === null) return;
      const draggedItem = inventory[index];
      if (!canPutDown(heldItem, draggedItem)) return;
      const isRightClick = isLastRightClick || syncLastRightClick;
      const isEmptyOrSame = ![null, heldItem.id].includes(draggedItem.id);
      if ((isRightClick && isEmptyOrSame) || draggedSlots.includes(index))
        return;
      if (!isRightClick && isEmptyOrSame) return;

      let newDraggedSlots = [...draggedSlots, index];
      let newInventory = [...inventory];
      let newHeldItem = { ...heldItem };

      if (isRightClick) {
        [newInventory, newHeldItem] = placeOneItem(
          newInventory,
          newHeldItem,
          index
        );
      } else {
        let newDragStash = [...dragStash];
        newDragStash.push(
          draggedItem.id === heldItem.id ? draggedItem.count : null
        );
        const distributedCount = Math.floor(
          newHeldItem.count / newDraggedSlots.length
        );
        if (distributedCount < 1) return;
        let remainder = newHeldItem.count % newDraggedSlots.length;
        newDraggedSlots.forEach((slot, index) => {
          if (newDragStash[index] !== null) {
            const stackSize = items[newHeldItem.id].stackSize;
            const totalCount = newDragStash[index] + distributedCount;
            if (totalCount > stackSize) {
              remainder += totalCount - stackSize;
              newInventory[slot].count = stackSize;
            } else {
              newInventory[slot].count = totalCount;
            }
          } else {
            newInventory[slot] = {
              id: newHeldItem.id,
              count: distributedCount,
            };
          }
        });
        setDragRemainder(remainder);
        setDragStash(newDragStash);
      }
      setInventory(type, newInventory);
      setHeldItem(newHeldItem);
      setDraggedSlots(newDraggedSlots);
    },
    [
      readonly,
      inventory,
      heldItem,
      isLastRightClick,
      draggedSlots,
      canPutDown,
      placeOneItem,
      setInventory,
      setHeldItem,
      type,
    ]
  );

  const handleFinishDrag = useCallback(() => {
    setDraggedSlots([]);
    if (dragRemainder >= 0) {
      setHeldItem(
        dragRemainder === 0 ? EMPTY_ITEM : { ...heldItem, count: dragRemainder }
      );
    }
    setDragRemainder(-1);
    setDragStash([]);
  }, [dragRemainder, heldItem, setHeldItem]);

  const handleSlotClick = useCallback(
    (index, isRightClick = false, isShift = false) => {
      if (readonly) return;
      let newInventory = [...inventory];
      let newHeldItem = { ...heldItem };
      const clickedItem = { ...newInventory[index] };

      if (isShift) {
        if (clickedItem.id === null) return;
        insertInventoryItem(
          [clickedItem],
          SHIFT_MOVE_TO[type][SCREENS[screen].id]
        );
        newInventory[index] = EMPTY_ITEM;
        pickUpCallback(isShift);
      } else if (newHeldItem.id !== null) {
        if (!canPutDown(newHeldItem, clickedItem)) {
          if (newHeldItem.id === clickedItem.id) {
            const totalCount = clickedItem.count + heldItem.count;
            const stackSize = items[newHeldItem.id].stackSize;
            if (totalCount <= stackSize) {
              newHeldItem.count = totalCount;
              newInventory[index] = EMPTY_ITEM;
              pickUpCallback(isShift);
            }
          }
        } else {
          const stackSize = items[newHeldItem.id].stackSize;
          if (!isRightClick) {
            if (checkDClickIndex !== index) {
              if (clickedItem.id === newHeldItem.id) {
                const totalCount = clickedItem.count + heldItem.count;
                if (totalCount <= stackSize) {
                  newInventory[index] = {
                    id: newHeldItem.id,
                    count: totalCount,
                  };
                  newHeldItem = EMPTY_ITEM;
                } else {
                  newInventory[index] = {
                    id: newHeldItem.id,
                    count: stackSize,
                  };
                  newHeldItem.count = totalCount - stackSize;
                }
              } else {
                [newInventory[index], newHeldItem] = [
                  heldItem,
                  clickedItem.id !== null ? clickedItem : EMPTY_ITEM,
                ];
              }
            } else {
              for (let i = 0; i < newInventory.length; i++) {
                if (newInventory[i].id === newHeldItem.id) {
                  if (newInventory[i].count + newHeldItem.count > stackSize) {
                    newInventory[i].count -= stackSize - newHeldItem.count;
                    newHeldItem.count = stackSize;
                    break;
                  }
                  newHeldItem.count += newInventory[i].count;
                  newInventory[i] = EMPTY_ITEM;
                }
              }
              setCheckDClickIndex(undefined);
            }
          } else {
            [newInventory, newHeldItem] = placeOneItem(
              newInventory,
              newHeldItem,
              index
            );
          }
        }
      } else {
        pickUpCallback(isShift);
        if (isRightClick && clickedItem.id !== null && allowRightClickPickup) {
          const halfStack = Math.ceil(clickedItem.count / 2);
          newHeldItem = { id: clickedItem.id, count: halfStack };
          newInventory[index].count -= halfStack;
          if (newInventory[index].count === 0) {
            newInventory[index] = EMPTY_ITEM;
          }
        } else {
          newHeldItem = clickedItem.id !== null ? clickedItem : EMPTY_ITEM;
          newInventory[index] = EMPTY_ITEM;
        }
      }
      setCheckDClickIndex(index);
      timeoutDClick();
      setInventory(type, newInventory);
      setHeldItem(newHeldItem);
      handleMouseLeave();
    },
    [
      readonly,
      inventory,
      heldItem,
      checkDClickIndex,
      items,
      canPutDown,
      allowRightClickPickup,
      insertInventoryItem,
      pickUpCallback,
      placeOneItem,
      setInventory,
      setHeldItem,
      timeoutDClick,
      type,
      handleMouseLeave,
    ]
  );

  const handleMouseOver = useCallback(
    (index) => {
      if (readonly) return;
      if (isDragging) handleDrag(index);
    },
    [readonly, isDragging, handleDrag]
  );

  const handleMouseDown = useCallback(
    (e, index) => {
      if (readonly) return;
      if (!isDragging && !e.shiftKey) {
        const isRightClick = e.button === 2;
        setMousePosition({ x: e.clientX, y: e.clientY });
        setIsLastRightClick(isRightClick);
        setIsDragging(true);
        if (checkDClickIndex !== index) {
          handleDrag(index, isRightClick);
        }
      }
      setLastMouseDownSlotIndex(index);
      setClickReference(type);
    },
    [
      readonly,
      isDragging,
      checkDClickIndex,
      handleDrag,
      setMousePosition,
      setIsLastRightClick,
      setClickReference,
      type,
    ]
  );

  const handleMouseUp = useCallback(
    (e, index) => {
      if (readonly) return;
      if (clickReference !== type) return;
      if (
        index === null &&
        (e.target.classList[0] !== "mc-grid" ||
          e.target.classList[1] !== `type-${type}`)
      ) {
        if (isDragging) handleFinishDrag();
        setIsDragging(false);
        return;
      }

      setIsDragging(false);
      if (lastMouseDownSlotIndex === index && draggedSlots.length === 0) {
        handleSlotClick(index, isLastRightClick, e.shiftKey);
      }
      if (index !== null) {
        handleFinishDrag();
      } else {
        setDraggedSlots([]);
      }
      setIsLastRightClick(null);
    },
    [
      readonly,
      clickReference,
      type,
      isDragging,
      lastMouseDownSlotIndex,
      draggedSlots,
      isLastRightClick,
      handleFinishDrag,
      handleSlotClick,
    ]
  );

  useEffect(() => {
    const eventHandler = (e) => handleMouseUp(e, null);
    window.addEventListener("mouseup", eventHandler);
    return () => window.removeEventListener("mouseup", eventHandler);
  }, [handleMouseUp]);

  useEffect(() => {
    setIsLeftDragging(isDragging && !isLastRightClick);
  }, [isDragging, isLastRightClick, setIsLeftDragging]);

  const contextValue = {
    type,
    inventory,
    heldItem,
    draggedSlots,
    isLastRightClick,
    isDragging,
    handleMouseOver,
    handleMouseDown,
    handleMouseUp,
  };

  return (
    <InventoryContext.Provider value={contextValue}>
      {children}
    </InventoryContext.Provider>
  );
};

export { InventoryProvider, useInventory };
